import React from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/translations';

const Owner = () => {
  const location = useLocation();
  const { language } = useLanguage();
  const t = translations[language];
  const isROPage = location.pathname === '/ro';

  const borderColor = isROPage ? 'border-blue-500/50' : 'border-green-500/50';
  const textColor = isROPage ? 'text-blue-400' : 'text-green-400';
  const gradientColor = isROPage ? 'from-blue-400 to-cyan-400' : 'from-green-400 to-cyan-400';
  const gradientLine = isROPage ? 'from-blue-500 to-cyan-500' : 'from-green-500 to-cyan-500';
  const bgGradient = isROPage ? 'bg-gradient-to-b from-blue-50 via-cyan-50 to-white' : 'bg-gradient-to-b from-gray-900 via-slate-800 to-gray-900';
  const cardBg = isROPage ? 'bg-white shadow-xl' : 'bg-gray-800/90 backdrop-blur-sm';
  const textMain = isROPage ? 'text-gray-900' : 'text-white';
  const textSecondary = isROPage ? 'text-gray-600' : 'text-gray-300';

  return (
    <section id="owner" className={`py-24 ${bgGradient} relative overflow-hidden ${isROPage ? 'text-gray-900' : 'text-white'}`}>
      {/* Background Pattern */}
      {!isROPage && <div className="absolute inset-0 security-grid opacity-5"></div>}
      
      {/* Corner Brackets - only for CCTV */}
      {!isROPage && (
        <>
          <div className={`absolute top-8 left-8 w-16 h-16 border-t-2 border-l-2 ${borderColor}`}></div>
          <div className={`absolute top-8 right-8 w-16 h-16 border-t-2 border-r-2 ${borderColor}`}></div>
        </>
      )}
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <div className={`inline-block px-6 py-2 ${isROPage ? 'bg-blue-100 border-2 border-blue-400' : 'bg-black/60 border-2 border-green-500/50'} rounded mb-6`}>
              <span className={`${isROPage ? 'text-blue-700' : textColor} font-semibold font-mono text-sm tracking-wider`}>{t.ownerBadge}</span>
            </div>
            <h2 className="text-5xl md:text-6xl font-extrabold mb-6 font-mono">
              {t.ownerTitle} <span className={`bg-gradient-to-r ${gradientColor} bg-clip-text text-transparent`}>{t.ownerTitle2}</span>
            </h2>
            <p className={`text-xl md:text-2xl ${isROPage ? 'text-gray-600' : 'text-gray-400'} max-w-3xl mx-auto font-mono`}>
              {t.ownerSubtitle}
            </p>
            <div className={`w-24 h-1 bg-gradient-to-r ${gradientLine} mx-auto mt-6 rounded-full`}></div>
          </div>

          {/* Owner Card */}
          <div className={`${cardBg} rounded-lg p-8 md:p-12 shadow-2xl border-2 ${borderColor} transform hover:scale-[1.01] transition-all duration-300`}>
            <div className="flex flex-col md:flex-row gap-8 items-center">
              {/* Owner Image Section */}
              <div className="flex-shrink-0">
                <div className={`w-48 h-48 rounded-full bg-gradient-to-br ${gradientColor} p-1 shadow-2xl ${isROPage ? 'shadow-blue-500/50' : 'shadow-green-500/50'}`}>
                  <div className={`w-full h-full rounded-full ${isROPage ? 'bg-white' : 'bg-gray-900'} flex items-center justify-center overflow-hidden`}>
                    {/* Owner image - place your image in public/images/owner.jpg or owner.png */}
                    <img 
                      src="/images/image.png" 
                      alt={t.ownerName}
                      className="w-full h-full object-cover rounded-full"
                      onError={(e) => {
                        // Fallback to icon if image not found
                        e.target.style.display = 'none';
                        const fallback = e.target.nextElementSibling;
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                    <div className={`w-full h-full rounded-full ${isROPage ? 'bg-blue-100' : 'bg-gray-800'} flex items-center justify-center hidden`}>
                      <svg className={`w-32 h-32 ${isROPage ? 'text-blue-600' : textColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                  </div>
                </div>
                <div className={`text-center mt-6 ${textMain}`}>
                  <h3 className="text-3xl font-bold mb-2 font-mono">{t.ownerName}</h3>
                </div>
              </div>

              {/* Owner Details */}
              <div className="flex-1 space-y-6">
                {/* Experience */}
                <div>
                  <div className={`flex items-center mb-3 ${isROPage ? 'text-blue-600' : textColor}`}>
                    <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h4 className="text-xl font-bold font-mono">{t.ownerExperience}</h4>
                  </div>
                  <p className={`${textSecondary} text-lg ml-9`}>15+ {language === 'en' ? 'Years' : 'वर्ष'}</p>
                </div>

                {/* Contact Details */}
                <div className={`pt-6 border-t-2 ${borderColor}`}>
                  <h4 className={`text-xl font-bold mb-4 ${isROPage ? 'text-blue-600' : textColor} font-mono`}>{t.ownerContact}</h4>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${gradientColor} flex items-center justify-center mr-4 ${isROPage ? 'shadow-blue-500/50' : 'shadow-green-500/50'} shadow-lg`}>
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div>
                        <p className={`text-sm ${textSecondary} font-mono`}>{t.ownerEmail}</p>
                        <a href="mailto:ashishtripathi348@gmail.com" className={`${isROPage ? 'text-blue-600 hover:text-blue-700' : 'text-green-400 hover:text-green-300'} transition-colors`}>
                          ashishtripathi348@gmail.com
                        </a>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${gradientColor} flex items-center justify-center mr-4 ${isROPage ? 'shadow-blue-500/50' : 'shadow-green-500/50'} shadow-lg`}>
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                      </div>
                      <div>
                        <p className={`text-sm ${textSecondary} font-mono`}>{t.ownerPhone}</p>
                        <a href="tel:+919795200337" className={`${isROPage ? 'text-blue-600 hover:text-blue-700' : 'text-green-400 hover:text-green-300'} transition-colors`}>
                          +91 9795200337
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Owner;
